import React, { forwardRef, useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  containerClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    id,
    label,
    hint,
    error,
    containerClassName,
    className,
    children,
    disabled,
    ...rest
  },
  ref
) {
  const reactId = useId();
  const fieldId = id || `select-${reactId}`;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label
          htmlFor={fieldId}
          className="block text-body-sm font-medium text-neutral-700 dark:text-neutral-300"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={fieldId}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
          className={cn(
            'block w-full appearance-none rounded-lg border bg-white dark:bg-neutral-900 px-3 py-2 pr-9 text-body text-neutral-900 dark:text-neutral-100',
            'transition-colors duration-150 ease-out',
            'dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-700',
            'focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-transparent',
            'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:dark:bg-neutral-900/60 dark:disabled:bg-neutral-800',
            error
              ? 'border-danger-400 focus:ring-danger-500 dark:border-danger-500'
              : 'border-neutral-300 dark:border-neutral-700',
            className
          )}
          {...rest}
        >
          {children}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500"
          aria-hidden="true"
        />
      </div>
      {hint && !error && (
        <p id={hintId} className="text-caption text-neutral-500 dark:text-neutral-400">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="text-caption text-danger-600 dark:text-danger-400"
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}
    </div>
  );
});

export default Select;
